import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import PageWrapper from 'components/UI/PageWrapper';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import * as routes from 'router/Routes';
import {
  fetchDevicesInRollout,
  rolloutDetailsSelector,
  setDeviceFilters as setRolloutDeviceFilters,
} from 'store/rolloutState/rolloutState';
import Loader from 'components/UI/Loader';
import Card from 'components/UI/Card';
import { CardHeaderTop, CardHeaderTopColumn } from '../styled';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { DeviceFilter, DeviceFilterAttribute } from 'models/rollout';
import { FormattedMessage } from 'react-intl';
import Pagination, { PagePagination } from 'components/UI/Pagination';
import CardContainer from 'components/UI/CardContainer';
import RolloutDeviceTable from 'components/rollout/RolloutDeviceTable';
import RolloutDeviceFilters from 'components/rollout/RolloutDeviceFilters';
import { metaSelector } from 'store';
import CardHeader from 'components/UI/Card/CardHeader';
import Button from 'components/UI/Button';
import FoldableCard from 'components/UI/FoldableCard';
import { ButtonGrid } from 'styles';
import {
  ContentContainer,
  NameContainer,
  Title,
} from 'components/rollout/RolloutInfoCard/styled';

// Initial pagination
const initialPagination = {
  limit: 10,
  offset: 0,
  page: 1,
};

const GroupDetailsPage: FC = () => {
  const dispatch = useAppDispatch();
  const { rolloutId, groupId } = useParams();

  const { isLoading, devices } = useAppSelector(rolloutDetailsSelector);
  const meta = useAppSelector(metaSelector);

  // Fetch rollout details if id selected
  useEffect(() => {
    if (rolloutId && groupId) {
      dispatch(
        fetchDevicesInRollout(
          rolloutId,
          [{ attribute: DeviceFilterAttribute.GROUP_ID, value: groupId }],
          0
        )
      );
    }
  }, [dispatch, rolloutId, groupId]);

  // Fetch devices in rollout
  const fetchDevices = useCallback(
    (deviceFilters: DeviceFilter[], offset?: number) => {
      if (rolloutId && groupId) {
        deviceFilters.push({
          attribute: DeviceFilterAttribute.GROUP_ID,
          value: groupId,
        });
        dispatch(fetchDevicesInRollout(rolloutId, deviceFilters, offset ?? 0));
      }
    },
    [dispatch, rolloutId, groupId]
  );

  // State
  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);

  // Paginate
  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      if (devices.filters.data) {
        fetchDevices(devices.filters.data, paginate.offset);
      } else {
        fetchDevices([], paginate.offset);
      }
    },
    [fetchDevices, devices.filters.data]
  );

  // Reset pagination
  const resetPagination = useCallback(
    () => setPagination(initialPagination),
    []
  );

  // Toggle filters
  const toggleFilters = useCallback(() => {
    dispatch(setRolloutDeviceFilters(!devices.filters.active));
  }, [dispatch, devices.filters.active]);

  // Apply filters
  const onFilter = useCallback(
    (deviceFilters: DeviceFilter[]) => {
      setPagination(initialPagination);
      fetchDevices(deviceFilters, initialPagination.offset);
    },
    [fetchDevices]
  );

  // Render results
  const results = useMemo(() => {
    if (devices.isLoading) {
      return <Loader center />;
    }
    if (!devices.data || devices.data.length === 0) {
      return null;
    }
    return <RolloutDeviceTable devices={devices} />;
  }, [devices]);

  if (isLoading) {
    return <Loader center />;
  }
  if (!rolloutId) {
    return null;
  }
  return (
    <PageWrapper>
      <Card>
        <CardHeader
          navUrl={`${routes.ROLLOUTS}/${rolloutId}`}
          title={'misc.back'}
        />
        <ContentContainer>
          <NameContainer>
            <Title>{`Group: ${groupId}`}</Title>
          </NameContainer>
          <ButtonGrid>
            <Button
              onClick={toggleFilters}
              backgroundColor="surface"
              color={devices.filters.active ? 'primaryDark' : 'onSurface'}
            >
              <FormattedMessage id="filters.btn" />
            </Button>
          </ButtonGrid>
        </ContentContainer>
      </Card>

      <FoldableCard open={devices.filters.active}>
        <Heading type={HeadingType.h3} uppercase>
          <FormattedMessage id="rollout.filter_title" />
        </Heading>

        <RolloutDeviceFilters
          onFilter={onFilter}
          onClear={resetPagination}
          data={meta.data}
        />
      </FoldableCard>

      <CardContainer>
        <Card>
          <CardHeaderTop>
            <CardHeaderTopColumn>
              <Heading type={HeadingType.h3} uppercase>
                <FormattedMessage id="rollout.table_title" />
              </Heading>
            </CardHeaderTopColumn>
          </CardHeaderTop>
          {results}
          <Pagination
            totalCount={devices.totalCount || 0}
            noItems={devices.data?.length}
            pagination={pagination}
            setPagination={onSetPagination}
          />
        </Card>
      </CardContainer>
    </PageWrapper>
  );
};
export default GroupDetailsPage;
